import { Typography, useMediaQuery } from '@mui/material';
import { Box, Button, Card, CircularProgress, Grid } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectEndDate,
    selectStartDate,
} from '../../../app/Slices/DateFilterSlice';
import {
    selectExperienceFilters,
    setExperienceFilters,
} from '../../../app/Slices/ExperienceFiltersSlice';
import { selectExperienceSearchFilter } from '../../../app/Slices/ExperienceSearchSlice';
import { useGetAllPublicHolidaysForYearAndCountryQuery } from '../../../app/Slices/PublicHolidaysSlice';
import {
    useGetAllAssetsQuery,
    useGetWishlistQuery,
} from '../../../app/Slices/SevenDayApiSlice';
import ExperienceListLoadingComponent from '../../../layout/styledcomponents/LoadingAnimations/ExperienceListLoadingComponent';
import { filterItemsBySearch } from '../../../shared/utils';
import ExperiencesListAll from './ExperiencesListAll';
import { sortExperiencesForListing } from './experienceListingSort';
import ExperienceMap from './Maps/ExperienceMap';
import { List, MapOutlined } from '@mui/icons-material';
import NoResultsAnimation from '../../../layout/styledcomponents/LoadingAnimations/NoResults';
import UserRole from '../../../shared/AuthRoles';

export const filterItemsByDateRange = (
    items: any[],
    startDate: any,
    endDate: any,
    publicHolidaysArray: any[],
): any[] => {
    if (!startDate || !endDate) {
        return items;
    }

    const dayNames = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
    ];
    let currentDate = new Date(startDate?.getTime());

    endDate = new Date(endDate?.getTime());
    currentDate?.setHours(0, 0, 0, 0);

    const daysAvailable = new Set<string>();
    while (currentDate <= endDate) {
        daysAvailable.add(dayNames[currentDate?.getDay()]);
        currentDate.setDate(currentDate?.getDate() + 1);
    }

    const daysArray = Array.from(daysAvailable);

    currentDate = new Date(startDate?.getTime());
    currentDate?.setHours(0, 0, 0, 0);

    const datesArray: Date[] = [];

    while (currentDate <= endDate) {
        datesArray.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return items.filter((item) => {
        if (!item.isAvailable) {
            return false;
        }

        for (const date of datesArray) {
            if (
                !item?.publicHolidays &&
                publicHolidaysArray
                    .filter((data) => {
                        return (
                            data?.counties?.includes(`AU-${item?.state}`) ||
                            data?.global
                        );
                    })
                    .some((holiday) => {
                        return (
                            new Date(holiday.date).setHours(0, 0, 0, 0) ===
                            date.getTime()
                        );
                    })
            ) {
                return false;
            }
        }

        for (const day of daysArray) {
            if (!item[day]) {
                return false;
            }
        }
        return true;
    });
};

export default function ExperiencesListAllComponent({
    visitType = '',
}: {
    visitType?: string;
}) {
    // const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 900px)');
    const isLaptop = useMediaQuery('(max-width: 1200px)');
    const dispatch = useDispatch();
    const ExperienceFilters = useSelector(selectExperienceFilters);
    const loggedInUser = UserRole();
    const isGuestUser = loggedInUser?.role?.includes('guest');
    const searchTerm = useSelector(selectExperienceSearchFilter);

    const [isLoading, setIsLoading] = useState(true);
    const { wishlist, ...restExperienceFilters } = ExperienceFilters;

    const { data, isFetching } = useGetAllAssetsQuery({
        url: '/modules',
        ...restExperienceFilters,
        ...(wishlist ? { wishlist: true } : {}),
        ...(visitType ? { visitType } : {}),
    });
    useEffect(() => {
        if (!isFetching) {
            const delay = setTimeout(() => {
                setIsLoading(false);
            }, 500);

            return () => clearTimeout(delay);
        } else {
            setIsLoading(true);
        }
    }, [isFetching]);

    const { data: wishlistItems = [] } = useGetWishlistQuery(undefined, {
        skip: !isGuestUser,
    });
    const wishlistedIds = (wishlistItems ?? []).map(
        (wishlistItem) => wishlistItem.id,
    );
    const sortedData = sortExperiencesForListing(data || []);

    const startDate = useSelector(selectStartDate);
    const endDate = useSelector(selectEndDate);
    const { data: publicHolidaysAustraliaCurrentYear } =
        useGetAllPublicHolidaysForYearAndCountryQuery({
            country: 'AU',
            year: new Date().getFullYear(),
        });
    const { data: publicHolidaysAustraliaNextYear } =
        useGetAllPublicHolidaysForYearAndCountryQuery({
            country: 'AU',
            year: new Date().getFullYear() + 1,
        });
    const { data: publicHolidaysAustraliaYearAfterNextYear } =
        useGetAllPublicHolidaysForYearAndCountryQuery({
            country: 'AU',
            year: new Date().getFullYear() + 2,
        });

    const publicHolidaysAustralia =
        publicHolidaysAustraliaCurrentYear &&
        publicHolidaysAustraliaNextYear &&
        publicHolidaysAustraliaYearAfterNextYear
            ? [
                  ...publicHolidaysAustraliaCurrentYear,
                  ...publicHolidaysAustraliaNextYear,
                  ...publicHolidaysAustraliaYearAfterNextYear,
              ]
            : [];

    const allAssets =
        data && publicHolidaysAustralia
            ? filterItemsByDateRange(
                  sortedData,
                  startDate,
                  endDate,
                  publicHolidaysAustralia,
              )
            : undefined;

    const filteredAssets = allAssets
        ? filterItemsBySearch(allAssets, searchTerm)
        : undefined;

    const [removedWishlistIds, setRemovedWishlistIds] = useState<number[]>([]);

    useEffect(() => {
        setRemovedWishlistIds([]);
    }, [wishlist, data]);

    const handleRemoveFromWishlist = (experienceId: number) => {
        if (!wishlist) {
            return;
        }
        setRemovedWishlistIds((prev) =>
            prev.includes(experienceId) ? prev : [...prev, experienceId],
        );
    };

    //Removing experience from the UI when wishlist filter is on and heart icon is clicked. This way we avoid a new backend call and a full refresh of experiences list
    const displayedAssets = filteredAssets?.filter(
        (experience: { id: number }) =>
            !removedWishlistIds.includes(experience.id),
    );

    const handleCloseMap = () => {
        setMapOpen(false);

        const hasMapBounds =
            ExperienceFilters.bounds != null ||
            ExperienceFilters.swLat != null ||
            ExperienceFilters.swLng != null ||
            ExperienceFilters.neLat != null ||
            ExperienceFilters.neLng != null;

        if (hasMapBounds) {
            dispatch(
                setExperienceFilters({
                    ...ExperienceFilters,
                    bounds: undefined,
                    swLat: undefined,
                    swLng: undefined,
                    neLat: undefined,
                    neLng: undefined,
                }),
            );
        }
    };

    const [mapOpen, setMapOpen] = useState(false);

    const shouldAutoOpenMap =
        Boolean(ExperienceFilters.bounds) ||
        ExperienceFilters.type?.[0] === 'vehicle';

    useEffect(() => {
        if (!shouldAutoOpenMap) {
            setMapOpen(false);
            return;
        }
        if (!isIpad) {
            setMapOpen(true);
        }
    }, [isIpad, shouldAutoOpenMap]);
    const isOwnerOrAdmin =
        loggedInUser?.role.includes('owner') || loggedInUser?.role === 'admin';

    const TOP_OFFSET = isOwnerOrAdmin && !isLaptop ? 234 : 274;

    return (
        <>
            <Box sx={{ display: 'flex', gap: 4, pt: 5 }}>
                {isIpad && mapOpen ? (
                    <></>
                ) : (
                    <Grid
                        container
                        spacing={4}
                        columns={60}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            paddingBottom: '20px',
                            height: '100%',
                        }}
                    >
                        {displayedAssets?.length === 0 ? (
                            <Grid
                                sx={{
                                    height: '100%',
                                    paddingTop: '50px',
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    alignContent: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                }}
                            >
                                <NoResultsAnimation
                                    logoSrc='/logos/7DAYTransparent/icon.png'
                                    message='No Results'
                                    size={140}
                                />
                            </Grid>
                        ) : isLoading || displayedAssets === undefined ? (
                            <ExperienceListLoadingComponent mapOpen={mapOpen} />
                        ) : (
                            <ExperiencesListAll
                                experiences={displayedAssets}
                                visitType={visitType}
                                mapOpen={mapOpen}
                                wishlistedIds={wishlistedIds}
                                onRemoveFromWishlist={handleRemoveFromWishlist}
                            />
                        )}
                    </Grid>
                )}
                {mapOpen && (
                    <Box
                        sx={{
                            position: 'sticky',
                            top: TOP_OFFSET,
                            width: isIpad ? '100%' : '50%',
                            flexShrink: 0,
                            alignSelf: 'flex-start',
                            display: 'flex',
                            flexDirection: 'column',
                            height: `calc(100vh - ${TOP_OFFSET + 30}px - var(--app-prompt-banner-height, 0px))`,
                        }}
                    >
                        <ExperienceMap
                            experiences={displayedAssets ?? []}
                            handleClose={handleCloseMap}
                        />

                        {isFetching && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    p: 1,
                                }}
                            >
                                <Card sx={{ px: 4, py: 1 }}>
                                    <CircularProgress size={24} />
                                </Card>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {isIpad && shouldAutoOpenMap && (
                <Box
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                        p: 2,
                    }}
                >
                    <Button
                        variant='contained'
                        disableElevation={false}
                        startIcon={mapOpen ? <List /> : <MapOutlined />}
                        onClick={() => setMapOpen(!mapOpen)}
                    >
                        {mapOpen ? 'List' : 'Map'}
                    </Button>
                </Box>
            )}
        </>
    );
}
